import type { Content, EditorOptions } from '@tiptap/react';
import { useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Link } from '@tiptap/extension-link';
import type { PlaceholderOptions } from '@tiptap/extension-placeholder';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Image } from '@tiptap/extension-image';
import { Markdown } from 'tiptap-markdown';
import {
  Table,
  TableRow,
  TableCell,
  TableHeader,
} from '@tiptap/extension-table';

import draftToHtml from 'draftjs-to-html';

import { tss } from 'tss-react/mui';

import { useEffect } from 'react';

import { CustomUnderline } from '../extensions/CustomUnderline';

export type TipTapVariant = 'none' | 'padded' | 'outlined';

const useStyles = tss
  .withParams<{
    variant: TipTapVariant;
    padding: [number, number];
  }>()
  .create(({ theme, variant, padding }) => {
    return {
      editor: {
        outline: 'none',
        border:
          variant === 'outlined'
            ? `1px solid ${theme.palette.grey[400]}`
            : 'none',
        '&:focus-visible': {
          outline: 'none',
          border:
            variant === 'outlined'
              ? `1px solid ${theme.palette.primary.main}`
              : 'none',
        },
        borderRadius: theme.shape.borderRadius,

        '& > p': {
          margin: 0,
        },
        padding: theme.spacing(...padding),
      },
      link: {
        color: theme.palette.primary.main,
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
      image: {
        maxWidth: '100%',
        height: 'auto',
      },
    };
  });

export type UseOr2EditorProps = Omit<Partial<EditorOptions>, 'content'> & {
  htmlContent?: Content;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  draftJsContent?: any;
  stringContent?: Content;
  variant?: TipTapVariant;
  // [vertical, horizontal]
  padding?: [number, number];
  editable?: boolean;
  placeholder?: string;
  placeholderProps?: Partial<PlaceholderOptions>;
  onChange?: (content: string) => void;
  immediatelyRender?: boolean;
};

export const useOr2Editor = ({
  htmlContent,
  draftJsContent,
  stringContent,
  placeholder = 'Type here...',
  placeholderProps,
  editable,
  variant = 'none',
  padding = [2, 1.5],
  onChange,
  immediatelyRender = false,
  ...other
}: UseOr2EditorProps) => {
  const { classes } = useStyles({ variant, padding });

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3],
          },
          // Configured separately below with custom options.
          link: false,
          underline: false,
        }),
        Link.configure({
          HTMLAttributes: {
            class: classes.link,
            target: '_blank',
            rel: 'noopener noreferrer',
          },
          autolink: true,
          openOnClick: !editable,
          defaultProtocol: 'https',
        }),
        CustomUnderline,
        Placeholder.configure({
          placeholder,
          ...placeholderProps,
        }),
        Image.configure({
          inline: true,
          HTMLAttributes: { class: classes.image },
        }),
        Markdown,
        Table,
        TableRow,
        TableCell,
        TableHeader,
      ],
      editorProps: {
        attributes: {
          class: classes.editor,
        },
      },
      content:
        htmlContent ??
        (draftJsContent && draftToHtml(draftJsContent)) ??
        stringContent,
      editable,
      onUpdate: ({ editor }) => {
        if (onChange) {
          onChange(editor.getHTML());
        }
      },
      immediatelyRender,
      shouldRerenderOnTransaction: false,
      ...other,
    },
    [editable],
  );

  useEffect(() => {
    if (!editable && !editor?.isDestroyed) {
      editor?.commands.setContent(
        htmlContent ??
          (draftJsContent && draftToHtml(draftJsContent)) ??
          stringContent,
        { emitUpdate: false },
      );
    }
  }, [htmlContent, draftJsContent, stringContent, editor, editable]);

  return editor;
};
